import { useRoute } from '#app'
import { useHead } from '@unhead/vue'
import { computed, type Ref } from 'vue'
import { useLayoutMode } from '~/composables/use-layout-mode'
import type { Organization } from '~/resources/graphql/graphql'

/**
 * Publish the current page's search policy in SSR HTML and after navigation.
 * Only explicitly public pages can opt into standalone or embedded indexing.
 */
export function useSearchIndexing(
    organization: Readonly<
        Ref<Pick<Organization, 'allowPublicSearchIndexing'> | null>
    >
) {
    const route = useRoute()
    const { isKiosk, isMobileApp, isGroup, isLightbox, isPrintMode } =
        useLayoutMode()

    const robots = computed(() => {
        // A failed organization lookup must not allow an embed exception.
        // Page purpose comes from route metadata, never the visitor's login state.
        if (
            organization.value === null ||
            isMobileApp.value ||
            isKiosk.value ||
            isGroup.value ||
            isPrintMode.value ||
            (route.meta.searchIndexing !== 'public' &&
                route.meta.searchIndexing !== 'embed')
        ) {
            return 'noindex'
        }

        // Public embeds remain eligible within the customer's website even when
        // the organization keeps standalone Redpoint pages out of search results.
        if (route.meta.searchIndexing === 'embed' || isLightbox.value) {
            return 'noindex, indexifembedded'
        }

        return organization.value.allowPublicSearchIndexing ? null : 'noindex'
    })

    useHead(() => {
        return {
            meta:
                robots.value === null
                    ? []
                    : [{ key: 'robots', name: 'robots', content: robots.value }]
        }
    })
}

/**
 * Exclude an error or confirmation while it replaces otherwise public content.
 * Unmounting restores the page policy without retaining an embed exception.
 */
export function useNoSearchIndexing() {
    useHead({
        meta: [
            {
                key: 'robots',
                name: 'robots',
                content: 'noindex',
                // Higher priority wins deduplication even if organization data refreshes.
                tagPriority: 'critical'
            }
        ]
    })
}
